import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const CareerTimelineSection = () => {
  const [expandedCards, setExpandedCards] = useState({});
  const cardsRef = useRef([]);

  const toggleCard = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardId = entry.target.id;
            const dotId = `dot-${cardId.replace('card-', '')}`;
            const btnId = `btn-${cardId.replace('card-', '')}`;

            const dotEl = document.getElementById(dotId);
            const btnEl = document.getElementById(btnId);

            if (dotEl && btnEl) {
              // Force reflow to restart animation
              void dotEl.offsetWidth;
              void btnEl.offsetWidth;

              // Add glow classes
              dotEl.classList.add('dot-glow-active');
              btnEl.classList.add('btn-glow-active');

              // Remove classes after animation completes
              const handleDotAnimEnd = () => {
                dotEl.classList.remove('dot-glow-active');
                dotEl.removeEventListener('animationend', handleDotAnimEnd);
              };
              const handleBtnAnimEnd = () => {
                btnEl.classList.remove('btn-glow-active');
                btnEl.removeEventListener('animationend', handleBtnAnimEnd);
              };

              dotEl.addEventListener('animationend', handleDotAnimEnd);
              btnEl.addEventListener('animationend', handleBtnAnimEnd);
            }
          }
        });
      },
      { threshold: 0.4 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => {
      cardsRef.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);

  const roles = [
    {
      id: 'avalara',
      badge: 'Current · May 2024 – Present',
      title: 'Account Executive',
      company: 'Avalara · Pune, India',
      description: 'Avalara is the global leader in cloud-based tax compliance automation.',
      pills: ['CFO Engagement', '$100K+ Avg Deal', 'Fortune 1000', 'Multi-Year Contracts', 'Channel + Direct'],
      current: true,
      metrics: [
        { value: '$1.21M', label: 'FY2024 ARR', sub: '122% of quota' },
        { value: '$1.41M', label: 'FY2025 ARR', sub: '107% of quota' },
        { value: '70+', label: 'New Logos', sub: 'Total acquired' },
        { value: '$2.5M+', label: 'Pipeline', sub: 'GTM events' }
      ],
      bullets: [
        'I drive new business and expansion revenue across enterprise and mid-market segments via direct and channel sales, engaging <strong>CFOs, Tax Heads, and Procurement leaders</strong> in complex multi-stakeholder processes to secure long-term enterprise contracts.',
        'Closed <strong>$1.21M in ARR at 122% of quota</strong> in FY2024 (9-month tenure, target: $990K) and continued with <strong>$1.41M at 107%</strong> in FY2025 (target: $1.32M), acquiring 30+ new logos.',
        'Represented Avalara at key industry events across India, owning on-ground GTM execution and generating <strong>$2.5M+ in qualified sales pipeline.</strong>',
        'Collaborated with solution engineers to architect integrations with payment gateways and enterprise systems, ensuring seamless implementation and customer success.',
        'Managed a mixed portfolio of new logo acquisition alongside upsell and cross-sell motions within existing accounts.'
      ],
      awards: ['🏆 Rookie of the Year — FY2024']
    },
    {
      id: 'builder',
      badge: 'Jun 2023 – Oct 2023',
      title: 'Business Development Manager',
      company: 'Builder.AI · Remote',
      description: 'Builder.AI was a high-growth unicorn providing a no-code AI-powered software development platform.',
      pills: ['AI / No-Code Platform', 'Technical Advisory', 'Channel Expansion', 'Enterprise BD'],
      metrics: [
        { value: '3', label: 'Enterprise Logos', sub: '4-month sprint' },
        { value: '3', label: 'Partnerships', sub: '2 ITES + 1 design' },
        { value: 'Unicorn', label: 'Company Stage', sub: 'AI-powered SaaS' }
      ],
      bullets: [
        'Responsible for enterprise and commercial segment new business acquisition — launched the BD motion from scratch, building pipeline and closing logos in a short 4-month tenure.',
        'Onboarded <strong>3 new enterprise logos within 4 months</strong>, demonstrating fast ramp and strong new business execution in a highly competitive market.',
        'Initiated and closed <strong>3 strategic partnerships</strong> (2 ITES firms + 1 design studio), expanding the indirect sales channel and building repeatable inbound deal flow.',
        'Guided customers through the complete SDLC — advising on <strong>tech stack selection, feature prioritisation, and platform architecture</strong> to align solutions with business objectives.'
      ],
      awards: []
    },
    {
      id: 'zoom',
      badge: 'Jun 2021 – May 2023',
      title: 'Account Executive',
      company: 'Zoom Video Communications · Pune, India',
      description: 'Zoom is the world\'s leading UCaaS platform.',
      pills: ['Tata Telecom Partnership', 'ISV / SDK Sales', 'Partner Ecosystem', 'PAN-India Territory'],
      metrics: [
        { value: '$2.1M', label: 'Total ARR', sub: '2-year tenure' },
        { value: '138%', label: 'Peak Quota', sub: 'FY2021' },
        { value: '124%', label: 'FY2022 Quota', sub: '$1.1M closed' },
        { value: '$290K', label: 'Channel ARR', sub: '10+ partners' }
      ],
      bullets: [
        'Part of India\'s Commercial and Digital Native sales team, I owned full deal cycles across a PAN-India territory — scaling platform adoption across enterprises, digital natives, and the partner ecosystem.',
        'Closed <strong>$2.1M in total ARR</strong> — delivering $620K at 138% in FY2021 (7-month ramp) and $1.1M at 124% in FY2022, consistently exceeding quota across both years.',
        'Spearheaded a strategic partnership with <strong>Tata Telecommunications</strong> to extend Zoom\'s footprint within Tata\'s install base and new customer base.',
        'Closed multiple <strong>ISV / SDK deals</strong> where customers embedded Zoom into their own products — working with engineering teams to convert technical evaluations into multi-year enterprise contracts.',
        'Built a channel network of <strong>10+ resellers and partners</strong>, driving $290K in indirect ARR (23% of total FY2023 sales), demonstrating strong partner-led execution.',
        'Co-led GTM strategy for the <strong>Zoom Events India launch</strong>, developing new use cases that secured 200 event licenses in the first month.'
      ],
      awards: ['⭐ MVP — 5 Consecutive Quarters', '🥇 Highest Indirect Deals via Partner']
    },
    {
      id: 'icertis',
      badge: 'Mar 2020 – May 2021',
      title: 'Sr. Consultant – Inside Sales',
      company: 'Icertis · Pune, India',
      description: 'Icertis is the global leader in Enterprise Contract Lifecycle Management (CLM) and among India\'s largest SaaS unicorns.',
      pills: ['$250M–$2B Segment', 'BFSI · IT/ITES · Pharma', 'C-Suite Selling', 'CLM / SaaS'],
      metrics: [
        { value: '$942K', label: 'Total ARR', sub: '14-month tenure' },
        { value: '102%', label: 'Quota FY2020', sub: '$663K on $650K' },
        { value: '10+', label: 'New Logos', sub: '$460K ARR' },
        { value: '5', label: 'Verticals', sub: 'Multi-industry' }
      ],
      bullets: [
        'Owned full-cycle sales for the growth enterprise segment — companies with $250M–$2B in revenue — navigating complex, multi-stakeholder sales cycles with C-suite buyers across BFSI, IT/ITES, Pharma, Manufacturing, and Consumer Goods.',
        'Closed <strong>$942K in total ARR</strong> across a 14-month tenure, achieving 102% of quota in FY2020 ($663K on a $650K target).',
        'Won <strong>10+ new logos contributing $460K in ARR</strong>, demonstrating consistent new business acquisition velocity across 5 verticals.',
        'Engaged C-suite and senior decision-makers — <strong>CLOs, CTOs, CFOs, CEOs, and Procurement Heads</strong> — to navigate complex enterprise sales cycles and secure executive buy-in.',
        'Managed territory across direct, partner, and ISV channels to maximise pipeline coverage and deal flow.',
        'Generated demand through <strong>targeted webinars and group demos</strong>, building qualified pipeline across multiple industry verticals.'
      ],
      awards: []
    },
    {
      id: 'salesforce',
      badge: 'Jan 2019 – Dec 2019',
      title: 'Sales Development Representative',
      company: 'Salesforce · Bangalore, India',
      description: 'Salesforce is the world\'s #1 CRM platform.',
      pills: ['Pipeline Generation', 'Outbound + Inbound', 'Multi-Cloud Portfolio', 'Enterprise Prospecting'],
      metrics: [
        { value: '$400K+', label: 'ACV', sub: '11 months' },
        { value: '$2.3M+', label: 'SQO Pipeline', sub: 'Generated' },
        { value: '5', label: 'Cloud Products', sub: 'Managed' }
      ],
      bullets: [
        'Part of the India Sales Development team, responsible for generating qualified pipeline across Sales, Service, Marketing, Analytics, and Platform clouds.',
        'Contributed <strong>$400K+ in ACV within 11 months</strong> — achieving the highest ACV contribution in FY2020 within the India SDR team.',
        'Generated <strong>$2.3M+ in sales-qualified pipeline</strong> through strategic outbound targeting and inbound lead conversion.',
        'Managed pipeline across all five Salesforce cloud products, building deep knowledge of enterprise CRM and CX selling motions.',
        'Represented Salesforce at multiple industry events across India, driving brand awareness and accelerating pipeline velocity.'
      ],
      awards: ['🥇 Highest ACV Contribution — FY2020, India SDR Team']
    },
    {
      id: 'searce',
      badge: 'Oct 2016 – Jan 2019',
      title: 'Cloud Consultant',
      company: 'Searce Inc · Pune, India (Google Premier Partner)',
      description: 'Searce is Google\'s Premier Partner in APAC.',
      pills: ['GCP · Cloud Migration', 'Google Workspace', 'Full-Cycle Sales', 'APAC Google Partner'],
      metrics: [
        { value: '₹9.5Cr+', label: 'Total Revenue', sub: '~$1.14M USD' },
        { value: '100%+', label: 'Quota', sub: 'Every quarter' },
        { value: '50+', label: 'Customers Won', sub: 'Google-Searce' },
        { value: '3', label: 'Products', sub: 'GCP · GWS · HRMS' }
      ],
      bullets: [
        'Full-cycle SaaS sales role covering Google Workspace, GCP, and HappierWork HRMS — working in direct liaison with Google India on joint GTM initiatives and strategic account planning.',
        'Closed <strong>₹9.5 Crores+ (~$1.14M USD)</strong> in total revenue across a 2.3-year tenure, consistently achieving the ₹40 Lakhs monthly quota every quarter.',
        'Won bids for <strong>50+ Google-Searce customers</strong>, managing complete sales cycles from prospecting through closure across cloud infrastructure and productivity solutions.',
        'Engaged <strong>CTOs, IT Heads, and business decision-makers</strong> to sell cloud migration and digital transformation solutions including Google Workspace and GCP.',
        'Drove account farming motions including <strong>cross-sell, upsell, renewals, and contract renegotiation</strong> to maximise customer lifetime value and reduce churn.',
        'Prepared and delivered RFI/RFP/RFQ responses, technical presentations, and product demonstrations across phone, email, and video.'
      ],
      awards: ['🏅 Top Salesperson — FY2017', '✓ Google Certified — GCP & Workspace']
    }
  ];

  return (
    <>
      <style>{`
        @keyframes dotGlow {
          0% { box-shadow: 0 0 0 rgba(255, 255, 255, 0); }
          25% { box-shadow: 0 0 22px rgba(255, 255, 255, 0.8); }
          50% { box-shadow: 0 0 8px rgba(255, 255, 255, 0.4); }
          75% { box-shadow: 0 0 22px rgba(255, 255, 255, 0.8); }
          100% { box-shadow: 0 0 0 rgba(255, 255, 255, 0); }
        }

        @keyframes btnGlow {
          0% { 
            background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(6, 182, 212, 0.2));
            box-shadow: 0 0 0 rgba(255, 255, 255, 0);
          }
          25% { 
            background: linear-gradient(135deg, rgba(139, 92, 246, 0.42), rgba(6, 182, 212, 0.42));
            box-shadow: 0 0 22px rgba(255, 255, 255, 0.6);
          }
          50% { 
            background: linear-gradient(135deg, rgba(139, 92, 246, 0.28), rgba(6, 182, 212, 0.28));
            box-shadow: 0 0 8px rgba(255, 255, 255, 0.3);
          }
          75% { 
            background: linear-gradient(135deg, rgba(139, 92, 246, 0.42), rgba(6, 182, 212, 0.42));
            box-shadow: 0 0 22px rgba(255, 255, 255, 0.6);
          }
          100% { 
            background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(6, 182, 212, 0.2));
            box-shadow: 0 0 0 rgba(255, 255, 255, 0);
          }
        }

        .dot-glow-active {
          animation: dotGlow 2s ease-out;
        }

        .btn-glow-active {
          animation: btnGlow 2s ease-out;
        }

        .career-card {
          transform: translateY(0px);
          transition: transform 0.3s cubic-bezier(.22,1,.36,1), box-shadow 0.3s cubic-bezier(.22,1,.36,1);
        }

        .career-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 16px 40px rgba(0,0,0,0.3);
        }
      `}</style>
      
      <section id="career" className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#2A1F5E]/20 via-transparent to-[#2A1F5E]/20" />
        
        <div className="max-w-[900px] mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-block mb-4">
              <span className="px-4 py-2 bg-white/10 backdrop-blur-md border border-[#8B5CF6]/30 rounded-full text-[#8B5CF6] text-xs font-mono font-semibold tracking-wider">
                CAREER JOURNEY
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="career-dashboard-heading">
              Career Dashboard
            </h2>
            <div className="flex justify-center mb-3">
              <div className="w-20 h-[3px] rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#06B6D4]" />
            </div>
            <p className="text-white/60 text-sm font-mono">9+ Years of Full-Cycle Enterprise Sales Excellence</p>
          </motion.div>

          <div className="relative pl-12">
            <div 
              className="absolute left-[20px] top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#8B5CF6] via-[#06B6D4] to-[#A78BFA]"
            />

            {roles.map((role, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.14, duration: 0.5 }}
                className="relative mb-6 last:mb-0"
              >
                <div 
                  id={`dot-${role.id}`}
                  className="absolute left-[-37px] top-[22px] w-[22px] h-[22px] rounded-full border-[3px] border-white z-10"
                  style={{ 
                    background: role.current ? '#8B5CF6' : '#06B6D4',
                    boxShadow: role.current ? '0 0 20px rgba(139, 92, 246, 0.6)' : '0 0 15px rgba(6, 182, 212, 0.4)'
                  }}
                >
                  {role.current && (
                    <div 
                      className="absolute inset-0 rounded-full animate-ping"
                      style={{ background: '#8B5CF6', opacity: 0.6 }}
                    />
                  )}
                </div>

                <div 
                  id={`card-${role.id}`}
                  ref={(el) => (cardsRef.current[index] = el)}
                  className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[14px] overflow-hidden career-card"
                >
                  <div className="p-[22px_24px]">
                    <div 
                      className="text-[10px] font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full inline-block mb-3"
                      style={{ 
                        background: role.current ? 'rgba(139, 92, 246, 0.2)' : 'rgba(6, 182, 212, 0.2)',
                        color: role.current ? '#A78BFA' : '#06B6D4'
                      }}
                    >
                      {role.badge}
                    </div>

                    <h3 className="text-[20px] font-semibold text-white mb-2">{role.title}</h3>
                    <p className="text-[15px] text-white/85 mb-2">{role.company}</p>
                    <p className="text-[13.5px] text-white/70 leading-[1.6] mb-4">{role.description}</p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {role.pills.map((pill, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1.5 rounded-[20px] text-[11px] text-white/80 bg-white/10 backdrop-blur-sm border border-white/5"
                        >
                          {pill}
                        </span>
                      ))}
                    </div>

                    <button
                      id={`btn-${role.id}`}
                      onClick={() => toggleCard(index)}
                      className="relative flex items-center gap-2 px-4 py-2 rounded-[20px] text-white text-sm font-medium transition-all overflow-hidden group"
                      style={{ 
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(6, 182, 212, 0.2))',
                        border: '1px solid rgba(139, 92, 246, 0.3)'
                      }}
                    >
                      <div 
                        className="absolute inset-0 bg-gradient-to-r from-[#8B5CF6]/20 to-[#06B6D4]/20 opacity-0 group-hover:opacity-100 transition-opacity animate-pulse"
                        style={{ animationDuration: '2s' }}
                      />
                      <span className="relative z-10">View full story</span>
                      <ChevronDown 
                        size={16} 
                        className="relative z-10 transition-transform duration-300"
                        style={{ transform: expandedCards[index] ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    </button>

                    <AnimatePresence>
                      {expandedCards[index] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-5 pt-5" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                              {role.metrics.map((metric, idx) => (
                                <div 
                                  key={idx}
                                  className="rounded-[10px] p-3 text-center bg-white/5 backdrop-blur-sm border border-white/5"
                                >
                                  <div className="text-[20px] font-bold mb-1" style={{ color: idx % 2 === 0 ? '#8B5CF6' : '#06B6D4' }}>
                                    {metric.value}
                                  </div>
                                  <div className="text-[10px] font-semibold uppercase text-white/65 mb-0.5">{metric.label}</div>
                                  <div className="text-[10px] text-white/55">{metric.sub}</div>
                                </div>
                              ))}
                            </div>

                            <div className="space-y-3 mb-4">
                              {role.bullets.map((bullet, idx) => (
                                <div key={idx} className="flex items-start gap-2">
                                  <span className="text-[#8B5CF6] mt-1">▸</span>
                                  <p 
                                    className="text-[13px] text-white/80 leading-[1.68]"
                                    dangerouslySetInnerHTML={{ __html: bullet }}
                                  />
                                </div>
                              ))}
                            </div>

                            {role.awards.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {role.awards.map((award, idx) => (
                                  <div 
                                    key={idx}
                                    className="px-3 py-2 rounded-md text-[12px] font-bold text-white bg-gradient-to-r from-[#8B5CF6]/20 to-[#06B6D4]/20 border border-[#8B5CF6]/30"
                                  >
                                    {award}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default CareerTimelineSection;